/**
 * Assignments API endpoints
 */

import { fetchApi, buildQueryString, withPagination } from '../core';
import type { QueryParams, CreateAssignmentData, UpdateAssignmentData, CreateTokenData } from '../types';
import type { Assignment, PaginatedResponse } from '@/types';
import { objectIdPath } from '../path';

type AssignmentListResponse = PaginatedResponse<Assignment[]> | Assignment[];

export function getAll(params?: QueryParams, componentName: string = 'Unknown') {
  const paginatedParams = withPagination(params);
  return fetchApi<AssignmentListResponse>(`/assignments${buildQueryString(paginatedParams)}`, {}, componentName);
}

export function getById(id: string, componentName: string = 'Unknown') {
  return fetchApi<Assignment>(`/assignments/${objectIdPath(id, 'assignment')}`, {}, componentName);
}

export function getForCourse(courseId: string, params?: QueryParams, componentName: string = 'Unknown') {
  const paginatedParams = withPagination(params);
  return fetchApi<AssignmentListResponse>(`/courses/${objectIdPath(courseId, 'course')}/assignments${buildQueryString(paginatedParams)}`, {}, componentName);
}

export function getForStudent(studentId: string, params?: QueryParams, componentName: string = 'Unknown') {
  const paginatedParams = withPagination(params);
  return fetchApi<AssignmentListResponse>(`/students/${objectIdPath(studentId, 'student')}/assignments${buildQueryString(paginatedParams)}`, {}, componentName);
}

export function create(data: CreateAssignmentData, componentName: string = 'Unknown') {
  return fetchApi<Assignment>('/assignments', {
    method: 'POST',
    body: JSON.stringify(data)
  }, componentName);
}

export function update(id: string, data: UpdateAssignmentData, componentName: string = 'Unknown') {
  return fetchApi<Assignment>(`/assignments/${objectIdPath(id, 'assignment')}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }, componentName);
}

export function remove(id: string, componentName: string = 'Unknown') {
  return fetchApi(`/assignments/${objectIdPath(id, 'assignment')}`, { method: 'DELETE' }, componentName);
}

// Token management
export function createToken(assignmentId: string, data: CreateTokenData, componentName: string = 'Unknown') {
  return fetchApi(`/assignments/${objectIdPath(assignmentId, 'assignment')}/tokens`, {
    method: 'POST',
    body: JSON.stringify(data)
  }, componentName);
}

export function listTokens(assignmentId: string, componentName: string = 'Unknown') {
  return fetchApi(`/assignments/${objectIdPath(assignmentId, 'assignment')}/tokens`, {}, componentName);
}

export function deactivateToken(assignmentId: string, tokenId: string, componentName: string = 'Unknown') {
  return fetchApi(`/assignments/${objectIdPath(assignmentId, 'assignment')}/tokens/${objectIdPath(tokenId, 'token')}`, {
    method: 'DELETE'
  }, componentName);
}
